import { api } from './api';
import { isDeviceReallyOn, subscriptionKeyMatches, type PushDeviceState } from '../utils/pushDeviceModel';

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** FIX-BUG-CARD-PUSH: never wait forever for a service worker that is not registered (then: no push here). */
async function swReady(timeoutMs = 8000): Promise<ServiceWorkerRegistration | null> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => window.setTimeout(() => resolve(null), timeoutMs)),
  ]);
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await swReady();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/** This browser's own subscription endpoint (null = none), so the server can say whether THIS browser is registered. */
export async function getCurrentPushEndpoint(): Promise<string | null> {
  if (!isPushSupported()) return null;
  try {
    return (await currentSubscription())?.endpoint ?? null;
  } catch {
    return null;
  }
}

async function saveOnServer(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  await api.subscribePush({
    endpoint: json.endpoint!,
    keys: { p256dh: json.keys!.p256dh, auth: json.keys!.auth },
  });
}

/**
 * FIX-BUG-CARD-PUSH: the REAL state of this device: permission + a browser subscription made with the server's
 * current key + saved on the server for me. A browser that is allowed and subscribed with the right key but unknown
 * to the server is re-saved silently (no prompt is needed for that), so it heals on the next visit.
 */
export async function getThisDevicePushState(): Promise<PushDeviceState> {
  const base: PushDeviceState = {
    supported: isPushSupported(), permission: 'unsupported', browserSubscribed: false, keyMatches: null, registeredOnServer: null, serverConfigured: false,
  };
  if (!base.supported) return base;
  base.permission = Notification.permission;
  let serverKey: string | null = null;
  try { serverKey = (await api.getVapidPublicKey()).data?.publicKey || null; } catch { /* unknown */ }
  base.serverConfigured = !!serverKey;
  let sub: PushSubscription | null = null;
  try { sub = await currentSubscription(); } catch { sub = null; }
  base.browserSubscribed = !!sub;
  base.keyMatches = sub ? subscriptionKeyMatches(sub.options?.applicationServerKey ?? null, serverKey) : null;
  try {
    const st = (await api.getPushDeviceStatus(sub?.endpoint ?? null)).data;
    base.registeredOnServer = sub ? !!st?.this_device_registered : false;
  } catch { base.registeredOnServer = null; }
  if (sub && base.permission === 'granted' && base.keyMatches !== false && base.registeredOnServer === false) {
    try {
      await saveOnServer(sub);
      base.registeredOnServer = true;
    } catch { /* stays not registered: the bar asks */ }
  }
  return base;
}

/** True only when notices really reach this browser (see getThisDevicePushState). */
export async function isPushSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    return isDeviceReallyOn(await getThisDevicePushState());
  } catch {
    return false;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * FIX-PUSH-REAL: the step that failed after "granted" (so the bar says WHAT failed instead of asking to approve again):
 * key = the server key could not be read, no_sw = the site's service worker is not ready, subscribe = the browser /
 * its push service refused (or did not answer), save = the server did not save this browser.
 */
export type PushFailStage = 'key' | 'no_sw' | 'subscribe' | 'save';

/** DQ-239: why a subscribe attempt did not end subscribed (so the screen can explain). */
export type PushSubscribeResult =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'denied' | 'not_configured' | 'failed'; stage?: PushFailStage; detail?: string };

/** FIX-PUSH-REAL: every screen that shows the push state listens to this (fired after a subscribe / unsubscribe here). */
export const PUSH_CHANGED_EVENT = 'ote-push-changed';

function announcePushChanged(): void {
  try { window.dispatchEvent(new Event(PUSH_CHANGED_EVENT)); } catch { /* no window */ }
}

/** A browser call that may never answer (the push service unreachable): give up after `ms`. */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_resolve, reject) => window.setTimeout(() => reject(new Error(`${label}: no answer after ${Math.round(ms / 1000)}s`)), ms)),
  ]);
}

function errorDetail(err: unknown): string {
  const e = err as { name?: unknown; message?: unknown } | null;
  const name = typeof e?.name === 'string' ? e.name : '';
  const message = typeof e?.message === 'string' ? e.message : String(err ?? '');
  return `${name && name !== 'Error' ? `${name}: ` : ''}${message}`.replace(/\s+/g, ' ').trim().slice(0, 300);
}

/** FIX-PUSH-REAL: the failure also reaches the server log (best effort; never throws). */
function reportSubscribeFailure(stage: PushFailStage, detail: string): void {
  try {
    void api.reportPushSubscribeFailure({ stage, detail, permission: Notification.permission }).catch(() => undefined);
  } catch { /* never block */ }
}

function failed(stage: PushFailStage, err: unknown): PushSubscribeResult {
  const detail = errorDetail(err);
  console.error(`Push subscribe failed (${stage}):`, err);
  reportSubscribeFailure(stage, detail);
  return { ok: false, reason: 'failed', stage, detail };
}

/** pushManager.subscribe may hang when the browser cannot reach its push service. */
const SUBSCRIBE_TIMEOUT_MS = 20000;

export async function subscribeToPush(): Promise<boolean> {
  return (await subscribeToPushDetailed()).ok;
}

/**
 * FIX-BUG-CARD-PUSH: the browser's permission window is opened FIRST, straight from the click (nothing awaited
 * before it, so the browser treats it as the user's action). Only after "granted": subscribe with the server's key
 * (an old subscription made with another key is replaced) and save it on the server.
 * `permission` lets a caller that already asked (in the same click) pass the answer in.
 */
export async function subscribeToPushDetailed(permissionPromise?: Promise<NotificationPermission>): Promise<PushSubscribeResult> {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  const permission = await (permissionPromise ?? Notification.requestPermission());
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  // FIX-PUSH-REAL: each step is tried on its own, so a failure says WHICH step failed (the bar shows it, the server logs it).
  let publicKey: string | null | undefined;
  try {
    publicKey = (await api.getVapidPublicKey()).data?.publicKey;
  } catch (err) {
    return failed('key', err);
  }
  if (!publicKey) return { ok: false, reason: 'not_configured' };

  let reg: ServiceWorkerRegistration | null;
  try {
    reg = await swReady();
  } catch (err) {
    return failed('no_sw', err);
  }
  if (!reg) return failed('no_sw', new Error('the service worker of the site is not active'));

  let subscription: PushSubscription | null;
  try {
    subscription = await reg.pushManager.getSubscription();
    if (subscription && subscriptionKeyMatches(subscription.options?.applicationServerKey ?? null, publicKey) === false) {
      // made with keys the server no longer has: every notice to it is refused (403) - replace it
      await subscription.unsubscribe().catch(() => undefined);
      subscription = null;
    }
    if (!subscription) {
      subscription = await withTimeout(reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      }), SUBSCRIBE_TIMEOUT_MS, 'pushManager.subscribe');
    }
  } catch (err) {
    return failed('subscribe', err);
  }

  try {
    await saveOnServer(subscription);
  } catch (err) {
    return failed('save', err);
  }
  announcePushChanged();
  return { ok: true };
}

/** Ask the browser's permission synchronously inside a click handler (before any await). */
export function requestPushPermissionNow(): Promise<NotificationPermission> {
  if (!isPushSupported()) return Promise.resolve('denied');
  try {
    return Notification.requestPermission();
  } catch {
    return Promise.resolve(Notification.permission);
  }
}

/**
 * FIX-PUSH-REAL: call `onChange` whenever the push state of this browser may have changed: the browser's notification
 * permission changed (also from another window / the lock icon), the user came back to this window, or a subscribe /
 * unsubscribe happened on another screen. Returns the cleanup.
 */
export function watchPushState(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  let stopped = false;
  let status: PermissionStatus | null = null;
  const fire = () => { if (!stopped) onChange(); };
  const onVisible = () => { if (document.visibilityState === 'visible') fire(); };
  window.addEventListener(PUSH_CHANGED_EVENT, fire);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', fire);
  try {
    void navigator.permissions?.query({ name: 'notifications' as PermissionName }).then((s) => {
      if (stopped) return;
      status = s;
      s.onchange = fire;
    }).catch(() => undefined);
  } catch { /* permissions API missing: visibility / focus still refresh */ }
  return () => {
    stopped = true;
    window.removeEventListener(PUSH_CHANGED_EVENT, fire);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', fire);
    if (status) status.onchange = null;
  };
}

export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const subscription = await currentSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await api.unsubscribePush(endpoint);
    }
    announcePushChanged();
    return true;
  } catch (err) {
    console.error('Push unsubscribe failed:', err);
    return false;
  }
}
